// src/hooks/usePersistedState.js
import { useState, useEffect } from 'react';

/**
 * Custom hook that persists state to sessionStorage
 * @param {string} key - The sessionStorage key
 * @param {*} defaultValue - Default value if nothing is stored or stored value is invalid
 * @param {function} validator - Optional function to validate stored values (should return boolean)
 * @returns {[*, function]} - [state, setState] tuple like useState
 */
const usePersistedState = (key, defaultValue, validator = null) => {
  // Initialize state with value from sessionStorage or default
  const [state, setState] = useState(() => {
    try {
      // Get value from sessionStorage
      const storedValue = sessionStorage.getItem(key);
      
      if (storedValue === null) {
        // No stored value, use default
        return defaultValue;
      }
      
      // Parse the stored value
      const parsedValue = JSON.parse(storedValue);
      
      // Validate the stored value if validator is provided
      if (validator && !validator(parsedValue)) {
        console.warn(`usePersistedState: Invalid stored value for key "${key}":`, parsedValue, 'Using default:', defaultValue);
        return defaultValue;
      }
      
      // Return the valid stored value
      return parsedValue;
      
    } catch (error) {
      // Handle JSON parsing errors or other storage errors
      console.warn(`usePersistedState: Error reading from sessionStorage for key "${key}":`, error, 'Using default:', defaultValue);
      return defaultValue;
    }
  });

  // Update sessionStorage whenever state changes
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      // Handle storage quota exceeded or other storage errors
      console.error(`usePersistedState: Error writing to sessionStorage for key "${key}":`, error);
    }
  }, [key, state]);

  return [state, setState];
};

export default usePersistedState;